/**
 * Customer portal estimates (tracker item 39): the linked customer's estimates
 * (never drafts or withdrawn ones) and one estimate with Approve / Decline
 * while it waits for an answer. Mobile first: stacked cards, 44px buttons.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { createAPIClient, IEstimatesApi } from '../../api';
import * as types from '../../api/types';
import { usePortalLoad } from './usePortalLoad';
import { EmptyBlock, FailureBlock, InlineAlert, LoadingBlock } from './PortalStates';
import { invoiceDate, invoiceMoney } from '../invoiceUi';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { CUSTOMER_STATUS_LABELS, bundleDayLabel, estimateErrorText, estimateStatusClass } from '../estimates/estimateUi';

export const PORTAL_ESTIMATES_PATH = '/portal/estimates';

function usePortalEstimatesApi(): IEstimatesApi {
  return useMemo(() => createAPIClient() as IEstimatesApi, []);
}

export function PortalEstimatesPage() {
  const api = usePortalEstimatesApi();
  const load = useCallback(() => api.listPortalEstimates({ page: 1, limit: 50 }), [api]);
  const { data, loading, failure, reload } = usePortalLoad(load, 'We could not load your estimates.');
  const rows = data?.data ?? [];
  return (
    <div>
      <h1 className="mb-3 text-lg font-bold text-gray-900">Estimates</h1>
      {loading && !data && <LoadingBlock label="Loading estimates" />}
      {failure && <FailureBlock failure={failure} onRetry={() => void reload()} />}
      {data && rows.length === 0 && <EmptyBlock>You have no estimates yet.</EmptyBlock>}
      {rows.length > 0 && (
        <ul className="space-y-3" aria-label="Estimates">
          {rows.map((e) => (
            <li key={e.id}>
              <Link
                to={`${PORTAL_ESTIMATES_PATH}/${e.id}`}
                className="block min-h-[44px] rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200 hover:ring-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="text-base font-semibold text-gray-900">Estimate {e.estimateNumber}</span>
                  <span className={`rounded px-2 py-0.5 text-xs font-semibold ${estimateStatusClass(e.status)}`}>{CUSTOMER_STATUS_LABELS[e.status]}</span>
                </div>
                {e.title && <div className="mt-1 break-words text-sm text-gray-700">{e.title}</div>}
                <div className="mt-1 break-words text-sm text-gray-600">{e.propertyAddress}</div>
                <div className="mt-2 flex justify-between text-sm">
                  <span className="text-gray-600">Valid until {invoiceDate(e.expiryDate)}</span>
                  <span className="font-semibold text-gray-900">{invoiceMoney(e.total)}</span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CustomerLine({ line }: { line: types.CustomerEstimateView['lines'][number] }) {
  return (
    <li className="py-3 text-sm" data-testid="estimate-line">
      <div className="flex justify-between gap-4">
        <span className="min-w-0 break-words font-medium text-gray-900">
          {line.bundleName && line.bundleOffsetDays !== null && line.bundleOffsetDays !== undefined && (
            <span className="mr-2 rounded bg-blue-100 px-1.5 py-0.5 text-xs font-semibold text-blue-900" data-testid="estimate-line-day">{bundleDayLabel(line.bundleOffsetDays)}</span>
          )}
          {line.description}
        </span>
        <span className="whitespace-nowrap font-semibold">{invoiceMoney(line.amount)}</span>
      </div>
      <div className="mt-0.5 text-xs text-gray-500">
        {line.quantity} × {invoiceMoney(line.unitPrice)}
        {Number(line.discount) > 0 && ` · discount −${invoiceMoney(line.discount)}`}
        {Number(line.tax) > 0 && ` · tax ${invoiceMoney(line.tax)}`}
      </div>
    </li>
  );
}

/** The lines and totals of an estimate as the customer sees it (portal and public link). */
export function CustomerEstimateBody({ estimate }: { estimate: types.CustomerEstimateView }) {
  return (
    <>
      {estimate.customerMessage && <p className="whitespace-pre-wrap break-words text-sm text-gray-800">{estimate.customerMessage}</p>}
      {(() => {
        // Lines of a service bundle (tracker item 85) are grouped under the bundle's name, each with its day of the program
        const plain = estimate.lines.filter((line) => !line.bundleName);
        const names = [...new Set(estimate.lines.filter((line) => line.bundleName).map((line) => line.bundleName as string))];
        return (
          <>
            {plain.length > 0 && <ul className="mt-3 divide-y divide-gray-100" aria-label="Estimate lines">{plain.map((line, index) => <CustomerLine key={index} line={line} />)}</ul>}
            {names.map((name) => (
              <section key={name} className="mt-4 rounded-md border border-blue-100" aria-label={`${name} visits`} data-testid="estimate-bundle">
                <h3 className="rounded-t-md bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-900">{name}</h3>
                <ul className="divide-y divide-gray-100 px-3">
                  {estimate.lines.filter((line) => line.bundleName === name).map((line, index) => <CustomerLine key={index} line={line} />)}
                </ul>
              </section>
            ))}
          </>
        );
      })()}
      <dl className="mt-3 ml-auto w-full max-w-xs space-y-1 text-sm" aria-label="Estimate totals">
        <div className="flex justify-between gap-6"><dt>Subtotal</dt><dd>{invoiceMoney(estimate.subtotal)}</dd></div>
        {estimate.discounts.map((d, i) => <div key={i} className="flex justify-between gap-6"><dt>Discount: {d.name}</dt><dd>−{invoiceMoney(d.amount)}</dd></div>)}
        <div className="flex justify-between gap-6"><dt>Tax</dt><dd>{invoiceMoney(estimate.totalTax)}</dd></div>
        <div className="flex justify-between gap-6 border-t-2 border-gray-800 pt-2 text-base font-bold"><dt>Total</dt><dd>{invoiceMoney(estimate.total)}</dd></div>
      </dl>
    </>
  );
}

export function PortalEstimatePage() {
  const { id = '' } = useParams<{ id: string }>();
  const api = usePortalEstimatesApi();
  const load = useCallback(() => api.getPortalEstimate(id), [api, id]);
  const { data: estimate, loading, failure, reload, setData } = usePortalLoad(load, 'We could not load this estimate.', id);
  const [mode, setMode] = useState<null | 'approve' | 'decline'>(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  if (loading && !estimate) return <LoadingBlock label="Loading estimate" />;
  if (failure) {
    return (
      <div className="space-y-3">
        <FailureBlock failure={failure} onRetry={failure.kind === 'not-found' ? undefined : () => void reload()} />
        <Link to={PORTAL_ESTIMATES_PATH} className="inline-flex min-h-[44px] items-center text-sm font-semibold text-blue-700 hover:underline">Back to estimates</Link>
      </div>
    );
  }
  if (!estimate) return null;

  async function answer(decision: types.EstimateDecision) {
    setBusy(true);
    setError(null);
    try {
      const saved = await api.respondPortalEstimate(id, decision, comment.trim() || null);
      setData(saved);
      setMode(null);
      setDone(decision === 'APPROVED' ? 'Thank you: the estimate is approved. We will contact you to schedule the work.' : 'The estimate was declined. Thank you for letting us know.');
    } catch (cause) {
      setError(estimateErrorText(cause, 'We could not record your answer. Please try again.'));
      void reload();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <Link to={PORTAL_ESTIMATES_PATH} className="inline-flex min-h-[44px] items-center text-sm font-semibold text-blue-700 hover:underline">&larr; Estimates</Link>
      <section className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
        <div className="flex items-start justify-between gap-3">
          <h1 className="text-lg font-bold text-gray-900">Estimate {estimate.estimateNumber}</h1>
          <span className={`rounded px-2 py-0.5 text-xs font-semibold ${estimateStatusClass(estimate.status)}`} data-testid="portal-estimate-status">
            {CUSTOMER_STATUS_LABELS[estimate.status]}
          </span>
        </div>
        {estimate.title && <p className="mt-1 break-words text-sm text-gray-700">{estimate.title}</p>}
        <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-gray-600">Property</dt><dd className="break-words">{estimate.propertyAddress}</dd>
          {estimate.issueDate && <><dt className="text-gray-600">Date</dt><dd>{invoiceDate(estimate.issueDate)}</dd></>}
          <dt className="text-gray-600">Valid until</dt><dd>{invoiceDate(estimate.expiryDate)}</dd>
        </dl>
      </section>

      <section aria-labelledby="estimate-lines-heading" className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
        <h2 id="estimate-lines-heading" className="mb-2 text-sm font-semibold text-gray-600">What is included</h2>
        <CustomerEstimateBody estimate={estimate} />
      </section>

      {done && <InlineAlert kind="success">{done}</InlineAlert>}
      {error && <InlineAlert>{error}</InlineAlert>}

      {estimate.canRespond && (
        <section aria-label="Your answer" className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
          {mode === null ? (
            <div className="flex flex-col gap-2 sm:flex-row">
              <button type="button" className={primaryButtonClass} onClick={() => setMode('approve')}>Approve estimate</button>
              <button type="button" className={secondaryButtonClass} onClick={() => setMode('decline')}>Decline</button>
            </div>
          ) : (
            <div>
              <label htmlFor="portal-estimate-comment" className={labelClass}>{mode === 'approve' ? 'Anything we should know? (optional)' : 'Why are you declining? (optional)'}</label>
              <textarea id="portal-estimate-comment" className={inputClass} rows={3} maxLength={1000} value={comment} onChange={(e) => setComment(e.target.value)} />
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <button type="button" className={primaryButtonClass} disabled={busy} onClick={() => void answer(mode === 'approve' ? 'APPROVED' : 'DECLINED')}>
                  {busy ? 'Sending…' : mode === 'approve' ? 'Confirm approval' : 'Confirm decline'}
                </button>
                <button type="button" className={secondaryButtonClass} onClick={() => setMode(null)}>Back</button>
              </div>
            </div>
          )}
        </section>
      )}
      {!estimate.canRespond && estimate.respondedAt && (
        <p className="text-sm text-gray-600">Answered {invoiceDate(estimate.respondedAt.slice(0, 10))}{estimate.responseComment ? `: “${estimate.responseComment}”` : ''}</p>
      )}
    </div>
  );
}
